import React, { useState, useEffect, useRef, useCallback } from 'react';
import px from 'prop-types';

export default function ScrollIndicator({ 
    autoHide = false,
    className = '',
    hideDelay = 2000,
    itemClass = '',
    targetSelector = '',
    showDots = false,
    showArrows = true,
}) {
    const [scrollProgress, setScrollProgress] = useState(0);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const timeoutRef = useRef(null);
    const containerRef = useRef(null);

    // Memoize the scroll handler to avoid recreating it
    const handleScroll = useCallback((container, items) => {
        const scrollLeft = container.scrollLeft;
        const scrollWidth = container.scrollWidth;
        const clientWidth = container.clientWidth;
        const maxScroll = scrollWidth - clientWidth;
        
        if (maxScroll > 0) {
            const progress = (scrollLeft / maxScroll) * 100;

            setScrollProgress(Math.min(100, Math.max(0, progress)));
            
            // Check if we're at the end (with 5px tolerance)
            if (scrollLeft >= maxScroll - 5) {
                setCurrentIndex(items.length - 1);
            } else {
                // Calculate current item index normally
                const itemWidth = scrollWidth / items.length;
                const index = Math.round(scrollLeft / itemWidth);

                setCurrentIndex(Math.min(items.length - 1, Math.max(0, index)));
            }
        } else {
            // If no scroll space, show first item as active
            setCurrentIndex(0);
        }

        // Auto-hide functionality
        if (autoHide) {
            setIsVisible(true);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                setIsVisible(false);
            }, hideDelay);
        }
    }, [autoHide, hideDelay]);

    useEffect(() => {
        const container = document.querySelector(targetSelector);
        
        if (!container) return;

        containerRef.current = container;

        const items = container.querySelectorAll(itemClass);
        const itemCount = items.length;

        const scrollHandler = () => handleScroll(container, items);

        container.addEventListener('scroll', scrollHandler);
        
        scrollHandler();
        
        requestAnimationFrame(() => {
            setTotalItems(itemCount);
        });

        return () => {
            container.removeEventListener('scroll', scrollHandler);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [targetSelector, itemClass, handleScroll]);

    const scrollToIndex = (index) => {
        const container = containerRef.current;

        if (!container) return;

        const items = container.querySelectorAll(itemClass);

        if (items[index]) {
            const itemWidth = container.scrollWidth / items.length;
            const scrollLeft = itemWidth * index;

            container.scrollTo({
                left: scrollLeft,
                behavior: 'smooth'
            });
        }
    };

    const scrollToNext = () => {
        if (currentIndex < totalItems - 1) {
            scrollToIndex(currentIndex + 1);
        }
    };

    const scrollToPrev = () => {
        if (currentIndex > 0) {
            scrollToIndex(currentIndex - 1);
        }
    };

    if (totalItems <= 1) return null;

    // Dots display mode
    if (showDots) {
        return (
            <div 
                className={`scroll-indicator scroll-indicator--dots ${className}`}
                style={{
                    opacity: isVisible ? 1 : 0.3,
                    transition: 'opacity 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '16px',
                }}
            >
                {Array.from({ length: totalItems }).map((_, index) => (
                    <button
                        key={index}
                        onClick={() => scrollToIndex(index)}
                        className={`scroll-indicator__dot ${index === currentIndex ? 'scroll-indicator__dot--active' : ''}`}
                        style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            background: index === currentIndex ? 'var(--primary, #3b82f6)' : '#cbd5e1',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            transform: index === currentIndex ? 'scale(1.3)' : 'scale(1)',
                        }}
                        aria-label={`Go to item ${index + 1}`}
                        aria-current={index === currentIndex ? 'true' : 'false'}
                    />
                ))}
            </div>
        );
    }

    // Progress bar display mode (original)
    return (
        <div 
            className={`scroll-indicator scroll-indicator--progress ${className}`}
            style={{
                opacity: isVisible ? 1 : 0.3,
                transition: 'opacity 0.3s ease',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                margin: '16px auto',
                width: '100%',
                position: 'relative',
            }}
        >
            {/* Previous Button */}
            {showArrows && (
                <button
                    onClick={scrollToPrev}
                    disabled={currentIndex === 0}
                    style={{
                        background: 'none',
                        border: 'none',
                        padding: '4px',
                        borderRadius: '50%',
                        cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
                        opacity: currentIndex === 0 ? 0.3 : 1,
                        transition: 'opacity 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    aria-label="Previous item"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="15,18 9,12 15,6"></polyline>
                    </svg>
                </button>
            )}

            {/* Progress Bar */}
            <div style={{
                flex: 1,
                height: '4px',
                backgroundColor: '#e5e7eb',
                borderRadius: '2px',
                overflow: 'hidden',
                position: 'relative'
            }}>
                <div
                    style={{
                        height: '100%',
                        backgroundColor: 'var(--primary)',
                        borderRadius: '2px',
                        width: `${scrollProgress}%`,
                        transition: 'width 0.1s ease'
                    }}
                />
            </div>

            {/* Item Counter */}
            <div style={{
                fontSize: '12px',
                color: '#6b7280',
                fontWeight: '500',
                minWidth: '40px',
                textAlign: 'center'
            }}>
                {currentIndex + 1}/{totalItems}
            </div>

            {/* Next Button */}
            {showArrows && (
                <button
                    onClick={scrollToNext}
                    disabled={currentIndex === totalItems - 1}
                    style={{
                        background: 'none',
                        border: 'none',
                        padding: '4px',
                        borderRadius: '50%',
                        cursor: currentIndex === totalItems - 1 ? 'not-allowed' : 'pointer',
                        opacity: currentIndex === totalItems - 1 ? 0.3 : 1,
                        transition: 'opacity 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    aria-label="Next item"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9,18 15,12 9,6"></polyline>
                    </svg>
                </button>
            )}
        </div>
    );
}

ScrollIndicator.propTypes = {
    autoHide: px.bool,
    className: px.string,
    hideDelay: px.number,
    itemClass: px.string,
    targetSelector: px.string,
    showDots: px.bool,
    showArrows: px.bool,
};